/**
 * Unified Position Cache Manager
 * 
 * This service coordinates between React Query cache and Position Store (Zustand)
 * to provide a single source of truth for position data across the application.
 * 
 * Key Features:
 * - Bidirectional synchronization between React Query and Position Store
 * - Cache hydration on hard refresh
 * - Consistent SSE event handling
 * - Conflict resolution for concurrent updates
 * - Performance optimization with intelligent caching
 */

import { QueryClient } from '@tanstack/react-query';
import { LiveTrade } from './api';
import { usePositionStore } from '../store/positionStore';

// SECURITY: Import user-aware cache utilities for secure cache operations
import { UserAwareCacheKeys } from '../utils/userAwareCacheKeys';

export interface CacheUpdateMetadata {
  source: 'sse' | 'rest' | 'hydration' | 'manual';
  timestamp: number;
  eventType?: string;
  reason?: string;
}

export interface CacheSyncOptions {
  force?: boolean;
  skipValidation?: boolean;
  metadata?: CacheUpdateMetadata;
}

export class UnifiedPositionCacheManager {
  private static instance: UnifiedPositionCacheManager | null = null;
  private queryClient: QueryClient | null = null;
  private isInitialized = false;
  private lastSyncTimestamp = 0;
  private syncInProgress = false;
  
  // Cache coordination
  private readonly SYNC_DEBOUNCE_MS = 100; // Prevent rapid-fire syncs
  private currentUserId: string | null = null; // SECURITY: Current user context
  
  constructor() {
    if (UnifiedPositionCacheManager.instance) {
      return UnifiedPositionCacheManager.instance;
    }
    UnifiedPositionCacheManager.instance = this;
  }
  
  static getInstance(): UnifiedPositionCacheManager {
    if (!UnifiedPositionCacheManager.instance) {
      UnifiedPositionCacheManager.instance = new UnifiedPositionCacheManager();
    }
    return UnifiedPositionCacheManager.instance;
  }

  /**
   * SECURITY: Set current user context for cache operations
   */
  setUserContext(userId: string): void {
    if (this.currentUserId !== userId) {
      console.log(`🔐 [UnifiedPositionCache] Switching user context: ${this.currentUserId?.substring(0, 20)}... → ${userId.substring(0, 20)}...`);
      
      // Clear previous user's data from both caches
      if (this.currentUserId && this.queryClient) {
        console.log('🧹 [UnifiedPositionCache] Clearing previous user data from React Query cache');
        this.queryClient.removeQueries({ 
          predicate: (query) => {
            const keyString = JSON.stringify(query.queryKey);
            return keyString.includes('liveTrades') && keyString.includes(this.currentUserId!);
          }
        });
      }
      
      // Coordinate with position store
      usePositionStore.getState().setUserContext(userId);
      
      this.currentUserId = userId;
    }
  }

  /**
   * SECURITY: Get user-aware cache key for live trades
   */
  private getCacheKey(): readonly unknown[] {
    if (!this.currentUserId) {
      console.log('ℹ️ [UnifiedPositionCache] Cache operations pending - waiting for user context');
      return ['disabled'];
    }
    return UserAwareCacheKeys.liveTrades(this.currentUserId);
  }
  
  /**
   * Map unified cache source types to SSE event source types
   */
  private mapSourceToEventSource(source: 'sse' | 'rest' | 'hydration' | 'manual'): 'initial_load' | 'reconnection' | 'manual_refresh' {
    switch (source) {
      case 'sse':
        return 'reconnection';
      case 'hydration':
        return 'initial_load';
      case 'rest':
      case 'manual':
      default:
        return 'manual_refresh';
    }
  }
  
  /**
   * Initialize the unified cache manager with React Query client
   */
  initialize(queryClient: QueryClient): void {
    this.queryClient = queryClient;
    this.isInitialized = true;
    console.log('🔄 [Unified Cache] Initialized with React Query client');
    
    // Perform initial hydration if React Query has cached data
    this.performInitialHydration();
  }
  
  /**
   * 🚀 HYDRATION: Initialize position store from React Query cache on startup
   */
  private performInitialHydration(): void {
    if (!this.queryClient) return;
    
    try {
      const cachedData = this.queryClient.getQueryData<LiveTrade[]>(this.getCacheKey()); // SECURITY: User-aware cache key
      
      if (cachedData && cachedData.length > 0) {
        console.log('💾 [Unified Cache] Found React Query cache on startup, hydrating position store');
        this.syncReactQueryToPositionStore(cachedData, {
          metadata: {
            source: 'hydration',
            timestamp: Date.now(),
            reason: 'initial_startup_hydration'
          }
        });
      } else {
        console.log('📭 [Unified Cache] Starting fresh - no cached positions from previous session');
      }
    } catch (error) {
      console.error('❌ [Unified Cache] Initial hydration failed:', error);
    }
  }
  
  /**
   * 🔄 SYNC: React Query Cache → Position Store
   */
  syncReactQueryToPositionStore(
    positions: LiveTrade[], 
    options: CacheSyncOptions = {}
  ): void {
    if (!this.isInitialized) {
      console.warn('⚠️ [Unified Cache] Cannot sync - not initialized');
      return;
    }
    
    const { metadata = { source: 'manual', timestamp: Date.now() } } = options;
    
    try {
      console.log(`🔄 [Unified Cache] Syncing React Query → Position Store (${positions.length} positions, source: ${metadata.source})`);
      
      // Get position store actions
      const store = usePositionStore.getState();
      
      // Create a synthetic position snapshot event
      const syntheticEvent = {
        type: 'position_snapshot' as const,
        data: {
          positions,
          timestamp: metadata.timestamp,
          user_id: 'cache_sync',
          total_count: positions.length,
          source: this.mapSourceToEventSource(metadata.source)
        }
      };
      
      // Use the existing handlePositionSnapshot method for consistency
      store.handlePositionSnapshot(syntheticEvent);
      
      console.log('✅ [Unified Cache] Position store synchronized from React Query cache');
      
    } catch (error) {
      console.error('❌ [Unified Cache] Failed to sync React Query → Position Store:', error);
    }
  }
  
  /**
   * 🔄 SYNC: Position Store → React Query Cache
   */
  syncPositionStoreToReactQuery(
    positions: LiveTrade[], 
    options: CacheSyncOptions = {}
  ): void {
    if (!this.isInitialized || !this.queryClient) {
      console.warn('⚠️ [Unified Cache] Cannot sync - not initialized');
      return;
    }
    
    const { metadata = { source: 'sse', timestamp: Date.now() } } = options;
    
    try {
      console.log(`🔄 [Unified Cache] Syncing Position Store → React Query (${positions.length} positions, source: ${metadata.source})`);
      
      // Update React Query cache with position data
      this.queryClient.setQueryData(this.getCacheKey(), positions); // SECURITY: User-aware cache key
      
      console.log('✅ [Unified Cache] React Query cache synchronized from position store');
      
    } catch (error) {
      console.error('❌ [Unified Cache] Failed to sync Position Store → React Query:', error);
    }
  }
  
  /**
   * 🚀 SSE EVENT: Handle SSE position events with unified updates
   */
  handleSSEPositionEvent(
    eventType: string,
    positions: LiveTrade[],
    metadata: CacheUpdateMetadata
  ): void {
    if (!this.isInitialized) return;
    
    // Prevent rapid-fire syncs
    const now = Date.now();
    if (this.syncInProgress || (now - this.lastSyncTimestamp) < this.SYNC_DEBOUNCE_MS) {
      console.log(`🚫 [Unified Cache] Debouncing SSE update (${now - this.lastSyncTimestamp}ms since last)`);
      return;
    }
    
    this.syncInProgress = true;
    this.lastSyncTimestamp = now;
    
    try {
      console.log(`📡 [Unified Cache] Processing SSE ${eventType} event with unified updates`);
      
      // Update position store first (primary for real-time)
      const store = usePositionStore.getState();
      const syntheticEvent = {
        type: 'position_snapshot' as const,
        data: {
          positions,
          timestamp: metadata.timestamp,
          user_id: 'sse_unified',
          total_count: positions.length,
          source: this.mapSourceToEventSource(metadata.source)
        }
      };
      
      store.handlePositionSnapshot(syntheticEvent);
      
      // Then sync to React Query cache (for persistence and sharing)
      this.syncPositionStoreToReactQuery(positions, { metadata });
      
      console.log('✅ [Unified Cache] SSE event processed with unified updates');
      
    } catch (error) {
      console.error('❌ [Unified Cache] SSE event handling failed:', error);
    } finally {
      this.syncInProgress = false;
    }
  }
  
  /**
   * 🔄 MANUAL: Force bidirectional synchronization
   */
  forceSynchronization(direction: 'toReactQuery' | 'toPositionStore' | 'bidirectional' = 'bidirectional'): void {
    if (!this.isInitialized) {
      console.warn('⚠️ [Unified Cache] Cannot force sync - not initialized');
      return;
    }
    
    try {
      console.log(`🔄 [Unified Cache] Force synchronizing (${direction})`);
      
      if (direction === 'toReactQuery' || direction === 'bidirectional') {
        const positionStoreData = usePositionStore.getState().positions;
        const positionsArray = Array.from(positionStoreData.values());
        this.syncPositionStoreToReactQuery(positionsArray, {
          metadata: { source: 'manual', timestamp: Date.now(), reason: 'force_sync' }
        });
      }
      
      if (direction === 'toPositionStore' || direction === 'bidirectional') {
        const reactQueryData = this.queryClient?.getQueryData<LiveTrade[]>(this.getCacheKey()); // SECURITY: User-aware cache key
        if (reactQueryData) {
          this.syncReactQueryToPositionStore(reactQueryData, {
            metadata: { source: 'manual', timestamp: Date.now(), reason: 'force_sync' }
          });
        }
      }
      
    } catch (error) {
      console.error('❌ [Unified Cache] Force synchronization failed:', error);
    }
  }
  
  /**
   * 🔍 DIAGNOSTICS: Get cache state comparison
   */
  getCacheStateComparison(): {
    reactQuery: { exists: boolean; count: number; lastUpdated?: number };
    positionStore: { exists: boolean; count: number; lastUpdated: number };
    inSync: boolean;
    recommendations: string[];
  } {
    const reactQueryData = this.queryClient?.getQueryData<LiveTrade[]>(this.getCacheKey()); // SECURITY: User-aware cache key
    const positionStoreData = usePositionStore.getState();
    
    const reactQueryCount = reactQueryData?.length || 0;
    const positionStoreCount = positionStoreData.positions.size;
    
    const inSync = reactQueryCount === positionStoreCount;
    const recommendations: string[] = [];
    
    if (!inSync) {
      recommendations.push(`Counts don't match: React Query(${reactQueryCount}) vs Position Store(${positionStoreCount})`);
    }
    
    if (!reactQueryData && positionStoreCount > 0) {
      recommendations.push('Position store has data but React Query cache is empty - sync needed');
    }
    
    if (reactQueryData && positionStoreCount === 0) {
      recommendations.push('React Query has data but position store is empty - hydration needed');
    }
    
    return {
      reactQuery: {
        exists: !!reactQueryData,
        count: reactQueryCount,
      },
      positionStore: {
        exists: positionStoreCount > 0,
        count: positionStoreCount,
        lastUpdated: positionStoreData.lastUpdate
      },
      inSync,
      recommendations
    };
  }
  
  /**
   * 🧹 CLEANUP: Reset all cache state
   */
  resetAllCaches(): void {
    try {
      console.log('🧹 [Unified Cache] Resetting all cache state');
      
      // Clear React Query cache
      this.queryClient?.removeQueries({ queryKey: this.getCacheKey() }); // SECURITY: User-aware cache key
      
      // Clear position store and user context
      usePositionStore.getState().clearUserData();
      
      console.log('✅ [Unified Cache] All caches reset');
    } catch (error) {
      console.error('❌ [Unified Cache] Cache reset failed:', error);
    }
  }
  
  /**
   * 🚨 SECURITY: Clear all position data for user transition
   */
  clearAllPositionData(): void {
    try {
      console.log('🧹 [Unified Cache] Clearing all position data for security');
      
      // Clear React Query caches (all live trade related)
      this.queryClient?.removeQueries({ 
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes('liveTrades') || keyString.includes('positions');
        }
      });
      
      // Clear position store completely
      usePositionStore.getState().clearUserData();
      
      console.log('✅ [Unified Cache] All position data cleared');
    } catch (error) {
      console.error('❌ [Unified Cache] Position data clearing failed:', error);
    }
  }
}

// Export singleton instance
export const unifiedPositionCache = UnifiedPositionCacheManager.getInstance();

// Export React hook for easy component access
export const useUnifiedPositionCache = () => {
  return {
    manager: unifiedPositionCache,
    syncFromReactQuery: (positions: LiveTrade[]) => 
      unifiedPositionCache.syncReactQueryToPositionStore(positions),
    syncToReactQuery: (positions: LiveTrade[]) => 
      unifiedPositionCache.syncPositionStoreToReactQuery(positions),
    forcSync: () => unifiedPositionCache.forceSynchronization(),
    getState: () => unifiedPositionCache.getCacheStateComparison(),
    reset: () => unifiedPositionCache.resetAllCaches()
  };
};

// Debug utilities for browser console
if (typeof window !== 'undefined') {
  (window as any).debugPositionCache = () => {
    const state = unifiedPositionCache.getCacheStateComparison();
    console.table(state);
    return state;
  };
  
  (window as any).syncPositionCache = (direction?: 'toReactQuery' | 'toPositionStore' | 'bidirectional') => {
    unifiedPositionCache.forceSynchronization(direction);
  };
  
  (window as any).resetPositionCache = () => {
    unifiedPositionCache.resetAllCaches();
  };
}